import {
  confirmSignUp,
  resendSignUpCode,
  autoSignIn,
  fetchAuthSession,
  ResendSignUpCodeInput,
} from "@aws-amplify/auth";
import { Button, Form, Input, Typography } from "antd";
import { ReactComponent as Logo } from "shared/assets/images/logo-strm.svg";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { useDispatch } from "react-redux";
import { loginReduced } from "explorer/reducers/auth";
import { LanguageChange } from "shared/components";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";
import LoadingButton from "shared/components/button/LoadingButton";
import { useEffect, useState } from "react";
import {
  dataLayerClickedEvent,
  dataLayerErrorEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import {
  DISTRO_PARAM,
  PRICING_PARAM,
  TOKEN,
} from "shared/constants/StorageConsts";
import { getLoggedUser } from "explorer/reducers/users";
import {
  getLoggedUserArtist,
  getUserDistroToken,
} from "explorer/reducers/artistas";
import { hasQuizPending } from "shared/helpers/auth-functions";
import { LoginErrorEventName } from "event-tracker/error-event-name";
import { SignUpButtonClickEventName } from "event-tracker/button-click-event-name";
import { getStrmClassVideos } from "explorer/reducers/strm-class";
import { getMonitoredData } from "explorer/reducers/monitored-data";
import { getUserSubscription } from "explorer/reducers/subscription";

const VerificacaoLogin: React.FC<RouteComponentProps> = (props) => {
  const { history, location } = props;
  const [form] = Form.useForm();
  const usuario: any = location.state;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);

  const onFinish = async () => {
    dataLayerClickedEvent(SignUpButtonClickEventName.VERIFY_CODE, {
      ...tagManagerEvent.SignUp.S4A_SIGN_UP_VERIFICATION,
    });

    setLoading(true);
    try {
      const values = await form.validateFields();
      const { isSignUpComplete } = await confirmSignUp({
        username: usuario.login,
        confirmationCode: values.codigo,
      });

      if (isSignUpComplete) {
        let autoSignedInSuccessed = false;
        try {
          const { isSignedIn } = await autoSignIn();
          autoSignedInSuccessed = isSignedIn;
        } catch (error: any) {
          toast.info<string>(t("pages.VerificacaoLogin.autoLoginFalho"));
          history.push("/login");
        }
        if (autoSignedInSuccessed) {
          const { idToken } = (await fetchAuthSession()).tokens ?? {};

          localStorage.setItem(TOKEN, idToken?.toString() ?? "");
          dispatch(getLoggedUser());
          dispatch(getLoggedUserArtist());
          dispatch(getMonitoredData());
          dispatch(loginReduced());
          dispatch(getStrmClassVideos());
          dispatch(getUserDistroToken());
          dispatch(getUserSubscription());
          const distroFlow = sessionStorage.getItem(DISTRO_PARAM) === "true";
          const pricingFlow = sessionStorage.getItem(PRICING_PARAM) === "true";
          if (hasQuizPending()) return history.push("/quiz");
          const returnURL = sessionStorage.getItem("returnURL");
          sessionStorage.removeItem("returnURL");

          if (distroFlow) {
            history.push("/distribua");
            return;
          }
          if (pricingFlow) {
            history.push("/pricing");
            return;
          }
          if (returnURL) {
            history.push(returnURL);
            return;
          }

          history.push("/", { login: true });
        }
      } else {
        toast.info<string>(t("pages.VerificacaoLogin.autoLoginFalho"));
        history.push("/login");
      }
    } catch (error: any) {
      dataLayerErrorEvent(LoginErrorEventName.INVALID_CODE, {
        ...tagManagerEvent.SignUp.S4A_SIGN_UP_VERIFICATION,
      });
      if (
        error.name === "CodeMismatchException" ||
        error.name === "ExpiredCodeException"
      )
        toast.error<string>(t("pages.VerificacaoLogin.codigoInvalido"), {
          autoClose: 10000,
        });
      else toast.error(error.message, { autoClose: 10000 });
    }
    setLoading(false);
  };

  const onClickReenvia = async () => {
    dataLayerClickedEvent(SignUpButtonClickEventName.RESEND_CODE, {
      ...tagManagerEvent.SignUp.S4A_SIGN_UP_VERIFICATION,
    });

    try {
      const param: ResendSignUpCodeInput = {
        username: usuario.login,
      };
      await resendSignUpCode(param);
    } catch (error: any) {
      console.log(error);
    }
    toast.info<string>(t("pages.VerificacaoLogin.mensagemReenvio"), {
      autoClose: 10000,
    });
  };

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.SignUp.S4A_SIGN_UP_VERIFICATION,
    });
  }, []);

  return (
    <div
      className="conteudo-centralizado login"
      style={{
        height: "100vh",
        backgroundColor: "#1a191a",
        padding: "14px 26px",
      }}
    >
      <div className="direction">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div className="logo-region">
            <Logo className="logo" style={{ width: isMobile ? 80 : 100 }} />
          </div>
          <LanguageChange.SelectLanguageChange />
        </div>
        <Form form={form} onFinish={onFinish}>
          <div className="content" style={{ marginTop: 154 }}>
            <div className="text">
              <Typography.Title style={{ color: "#F2F2F2", fontSize: 20 }}>
                {t("pages.VerificacaoLogin.titulo")}
              </Typography.Title>
              <Typography.Text style={{ color: "#FFFFFF" }}>
                {t("pages.VerificacaoLogin.verificacaoMensagem")}
              </Typography.Text>
            </div>
            <div className="input">
              <Form.Item
                name="codigo"
                rules={[
                  {
                    required: true,
                    message: t("pages.VerificacaoLogin.codigoObrigatorio"),
                  },
                ]}
                normalize={(value) => value?.trim()}
              >
                <Input placeholder={t("pages.VerificacaoLogin.codigo")} />
              </Form.Item>
            </div>

            <div className="enter-button">
              <LoadingButton
                className="botao-roxo"
                style={{ marginBottom: "16px" }}
                loading={loading}
              >
                {t("pages.VerificacaoLogin.submit")}
              </LoadingButton>
            </div>
            <div className="esqueci-senha">
              <Button
                type="link"
                htmlType="button"
                onClick={onClickReenvia}
                className="button-reset-senha"
                style={{ margin: 0 }}
              >
                {t("pages.VerificacaoLogin.reenvioCodigo")}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default VerificacaoLogin;
